import React, { useEffect, useState, useRef } from 'react';
import InventoryService from '../../../services/InventoryService';
import { toast } from '../V2Components';
import { randomId } from '../../../utils/randomId';

const InputField = ({ label, id, placeholder, value, onChange, type = "text", icon = null, ...inputProps }) => (
    <div className="space-y-2">
        <label htmlFor={id} className="block text-sm font-semibold text-[var(--text-secondary)] px-1 font-['Plus_Jakarta_Sans']">{label}</label>
        <div className="relative">
            <input
                id={id}
                type={type}
                value={value || ''}
                onChange={onChange}
                placeholder={placeholder}
                className="w-full bg-[var(--bg-card)] border-0 rounded-xl py-4 px-6 focus:ring-2 focus:ring-[var(--primary)] text-[var(--text-main)] shadow-[var(--shadow-sm)] transition-shadow font-['Plus_Jakarta_Sans']"
                {...inputProps}
            />
            {icon && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]">
                    {icon}
                </div>
            )}
        </div>
    </div>
);

const AssetForm = ({ formData, onChange, rooms = [] }) => {
    const [showAdditional, setShowAdditional] = useState(false);
    const [categories, setCategories] = useState([]);

    // Manage properties state locally for smooth editable grid
    const [propertiesList, setPropertiesList] = useState([]);
    const isLocalPropChange = useRef(false);

    useEffect(() => {
        if (!isLocalPropChange.current) {
            const props = formData.properties || {};
            setPropertiesList(prev => {
                const existingIds = new Map(prev.map(p => [p.key, p.id]));
                return Object.entries(props)
                    .filter(([, v]) => {
                        if (v === null || v === undefined) return false;
                        if (typeof v === 'string') return v.trim() !== '';
                        if (Array.isArray(v)) return v.length > 0;
                        if (typeof v === 'object') return Object.keys(v).length > 0;
                        return true;
                    })
                    .map(([k, v]) => ({
                        id: existingIds.get(k) || randomId(),
                        key: k,
                        value: typeof v === 'object' ? JSON.stringify(v) : String(v)
                    }));
            });
        }
        isLocalPropChange.current = false;
    }, [formData.properties]);

    const updateParentProperties = (newList) => {
        isLocalPropChange.current = true;
        const newProps = {};
        newList.forEach(item => {
            if (item.key.trim()) {
                newProps[item.key.trim()] = item.value;
            }
        });
        onChange({ properties: newProps });
    };

    const handleAddProperty = () => {
        const newList = [{ id: randomId(), key: '', value: '' }, ...propertiesList];
        setPropertiesList(newList);
        updateParentProperties(newList);
    };

    const handlePropertyChange = (id, field, val) => {
        const newList = propertiesList.map(item => item.id === id ? { ...item, [field]: val } : item);
        setPropertiesList(newList);
        updateParentProperties(newList);
    };

    const handleRemoveProperty = (id) => {
        const newList = propertiesList.filter(item => item.id !== id);
        setPropertiesList(newList);
        updateParentProperties(newList);
    };

    useEffect(() => {
        let cancelled = false;
        InventoryService.getCategories()
            .then(data => { if (!cancelled) setCategories(Array.isArray(data) ? data : []); })
            .catch(err => {
                console.error('Failed to load categories', err);
                if (!cancelled) toast.error('Could not load categories. Please refresh.');
            });
        return () => { cancelled = true; };
    }, []);

    const handleChange = (field) => (e) => {
        if (field === 'category') {
            onChange({ category: e.target.value, item_subcategory: '' });
        } else {
            onChange({ [field]: e.target.value });
        }
    };

    const selectedCategoryData = categories.find(c => c.value === formData.category);
    const subcategories = selectedCategoryData ? selectedCategoryData.subcategories || [] : [];

    // Find if current subcategory matches any existing subcategory ignoring case
    let displaySubcategory = formData.item_subcategory || '';
    if (displaySubcategory) {
        const matched = subcategories.find(s => s.toLowerCase() === displaySubcategory.toLowerCase());
        if (matched) {
            displaySubcategory = matched;
        }
    }

    // Auto-expand additional details if notes exist
    useEffect(() => {
        if (formData.notes) {
            setShowAdditional(true);
        }
    }, [formData.notes]);

    return (
        <div className="space-y-10">
            <div className="flex items-center justify-between md:hidden">
                <h3 className="text-[var(--primary)] font-bold text-lg font-['Manrope']">Manual Entry</h3>
                <span className="h-[1px] flex-1 ml-4 bg-[var(--border)]"></span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                {/* Asset Name - spans full width on md */}
                <div className="md:col-span-2">
                    <InputField
                        label="Asset Name"
                        id="asset-name"
                        value={formData.name}
                        onChange={handleChange('name')}
                        placeholder="e.g. Smart Refrigerator"
                    />
                </div>

                <div className="md:col-span-2 space-y-2">
                    <label htmlFor="asset-category" className="block text-sm font-semibold text-[var(--text-secondary)] px-1 font-['Plus_Jakarta_Sans']">Category</label>
                    <select
                        id="asset-category"
                        value={categories.length === 0 ? '' : (formData.category || '')}
                        onChange={handleChange('category')}
                        disabled={categories.length === 0}
                        className="w-full bg-[var(--bg-card)] border-0 rounded-xl py-4 px-6 focus:ring-2 focus:ring-[var(--primary)] text-[var(--text-main)] shadow-[var(--shadow-sm)] appearance-none font-['Plus_Jakarta_Sans'] disabled:opacity-60 disabled:cursor-wait"
                    >
                        {categories.length === 0 ? (
                            <option value="">Loading categories…</option>
                        ) : (
                            <>
                                <option value="">Select a category</option>
                                {categories.map(c => (
                                    <option key={c.value} value={c.value}>{c.label || c.value}</option>
                                ))}
                            </>
                        )}
                    </select>
                </div>

                {subcategories.length > 0 && (
                    <div className="md:col-span-2 space-y-2">
                        <label htmlFor="asset-subcategory" className="block text-sm font-semibold text-[var(--text-secondary)] px-1 font-['Plus_Jakarta_Sans']">Subcategory (Optional)</label>
                        <select
                            id="asset-subcategory"
                            value={displaySubcategory}
                            onChange={handleChange('item_subcategory')}
                            className="w-full bg-[var(--bg-card)] border-0 rounded-xl py-4 px-6 focus:ring-2 focus:ring-[var(--primary)] text-[var(--text-main)] shadow-[var(--shadow-sm)] appearance-none font-['Plus_Jakarta_Sans']"
                        >
                            <option value="">Select a subcategory</option>
                            {subcategories.map(sub => (
                                <option key={sub} value={sub}>{sub.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                            ))}
                            {displaySubcategory && !subcategories.includes(displaySubcategory) && (
                                <option value={displaySubcategory}>{displaySubcategory}</option>
                            )}
                        </select>
                    </div>
                )}

                <InputField
                    label="Manufacturer"
                    id="asset-manufacturer"
                    value={formData.manufacturer}
                    onChange={handleChange('manufacturer')}
                    placeholder="e.g. Samsung"
                />

                <InputField
                    label="Model Number"
                    id="asset-model"
                    value={formData.model}
                    onChange={handleChange('model')}
                    placeholder="e.g. RF28R7351SR"
                />

                <InputField
                    label="Serial Number"
                    id="asset-serial"
                    value={formData.serial}
                    onChange={handleChange('serial')}
                    placeholder="e.g. SN-9872345001"
                />

                {/* Color/Finish — top-level Item column (see schemas/item.py
                    BaseProperties._reject_promoted_keys). Storing color inside
                    properties causes a 400. Surface it as a first-class field
                    so users and AI scanners route it correctly. */}
                <InputField
                    label="Color/Finish"
                    id="asset-color"
                    value={formData.color}
                    onChange={handleChange('color')}
                    placeholder="e.g. Stainless Steel"
                />

                {/* Location / Room - populated from user's rooms via API */}
                <div className="space-y-2">
                    <label htmlFor="asset-location" className="block text-sm font-semibold text-[var(--text-secondary)] px-1 font-['Plus_Jakarta_Sans']">Location / Room</label>
                    <select
                        id="asset-location"
                        value={formData.room_id || ''}
                        onChange={handleChange('room_id')}
                        className="w-full bg-[var(--bg-card)] border-0 rounded-xl py-4 px-6 focus:ring-2 focus:ring-[var(--primary)] text-[var(--text-main)] shadow-[var(--shadow-sm)] appearance-none font-['Plus_Jakarta_Sans']"
                    >
                        <option value="">Select a room</option>
                        {rooms.map(room => (
                            <option key={room.id} value={room.id}>{room.name}</option>
                        ))}
                    </select>
                </div>

                <InputField
                    label="Purchase Date"
                    id="asset-date"
                    type="date"
                    value={formData.date}
                    onChange={handleChange('date')}
                />

                {/* Purchase Price / Estimated Value */}
                <InputField
                    label="Purchase Price"
                    id="asset-value"
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*[.,]?[0-9]*"
                    value={formData.value}
                    onChange={handleChange('value')}
                    placeholder="e.g. 499.99"
                    icon={
                        <span className="text-sm font-semibold text-[var(--text-muted)]">$</span>
                    }
                />

                {/* Editable Properties Display */}
                <div className="md:col-span-2 space-y-4 mt-4">
                    <div className="flex items-center gap-2">
                        <label className="block text-sm font-semibold text-[var(--text-secondary)] px-1 font-['Plus_Jakarta_Sans']">Properties</label>
                        <button
                            type="button"
                            onClick={handleAddProperty}
                            className="w-6 h-6 rounded-full bg-[var(--primary-bg)] text-[var(--primary)] flex items-center justify-center hover:bg-[var(--primary-bg-dim)] transition-colors"
                            aria-label="Add Property"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                        </button>
                    </div>

                    <div className="space-y-3">
                        {propertiesList.map((prop) => (
                            <div key={prop.id} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-center">
                                <input
                                    value={prop.key}
                                    onChange={(e) => handlePropertyChange(prop.id, 'key', e.target.value)}
                                    placeholder="Property Name (e.g. Material)"
                                    className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl py-3 px-4 focus:ring-2 focus:ring-[var(--primary)] text-sm text-[var(--text-main)] font-semibold"
                                />
                                <input
                                    value={prop.value}
                                    onChange={(e) => handlePropertyChange(prop.id, 'value', e.target.value)}
                                    placeholder="Value"
                                    className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl py-3 px-4 focus:ring-2 focus:ring-[var(--primary)] text-sm text-[var(--text-main)]"
                                />
                                <button
                                    type="button"
                                    onClick={() => handleRemoveProperty(prop.id)}
                                    className="p-2 text-[var(--text-muted)] hover:text-[var(--error)] hover:bg-[var(--error-container)] rounded-lg transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                                </button>
                            </div>
                        ))}
                        {propertiesList.length === 0 && (
                            <p className="text-sm text-[var(--text-muted)] italic px-1">No properties added yet.</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Additional Details Toggle */}
            <div className="pt-4">
                <button
                    type="button"
                    onClick={() => setShowAdditional(!showAdditional)}
                    className="flex items-center gap-2 text-[var(--secondary)] font-semibold hover:opacity-80 transition-opacity"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={showAdditional ? "rotate-45 transition-transform" : "transition-transform"}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="16"></line>
                        <line x1="8" y1="12" x2="16" y2="12"></line>
                    </svg>
                    {showAdditional ? 'Hide Maintenance Details' : 'Add Warranty & Maintenance Details'}
                </button>

                {showAdditional && (
                    <div className="mt-8 animate-in slide-in-from-top-4 fade-in duration-300">
                        <div className="space-y-2">
                            <label htmlFor="asset-notes" className="block text-sm font-semibold text-[var(--text-secondary)] px-1 font-['Plus_Jakarta_Sans']">Notes & Details</label>
                            <textarea
                                id="asset-notes"
                                value={formData.notes || ''}
                                onChange={handleChange('notes')}
                                rows="3"
                                placeholder="Add warranty expiration, maintenance schedule, or links..."
                                className="w-full bg-[var(--bg-card)] border-0 rounded-xl py-4 px-6 focus:ring-2 focus:ring-[var(--primary)] text-[var(--text-main)] shadow-[var(--shadow-sm)] transition-shadow font-['Plus_Jakarta_Sans'] resize-y"
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AssetForm;
